import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import UserContext from "../../state/UserContext";
import IndividualComment from "./IndividualComment";
import { UserBasicDetailsDto } from "../../types/dtos/generic";
import { SavedCommentDto } from "../../types/dtos/forms";

interface CommentsListProps {
  /** The comments list to render */
  comments: SavedCommentDto[] | null;

  /** The form participants, for rendering avatars with */
  participants: UserBasicDetailsDto[];

  /** Whether or not this is readonly - can show "Reply" and "Delete" controls etc if set to false */
  isReadOnly: boolean;

  /** Optional - add css class names to the containing div */
  containerClassNames?: string;

  /** The event to call when the user clicks "Reply" against a comment */
  onTargetCommentForReply(targetCommentId: string): void;

  /** The method to call when the delete button is clicked against a comment */
  onDeleteComment(
    commentId: string,
    successCallback: () => void,
    errorCallback: () => void
  ): void;
}

/** A list of comments for the current question */
function CommentsList({
  comments,
  participants,
  isReadOnly,
  onDeleteComment,
  onTargetCommentForReply,
  containerClassNames = "",
}: CommentsListProps) {
  const userContext = useContext(UserContext);
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const handleDelete = (commentId: string) => {
    const successCallback = () => {
      setIsDeleting(false);
    };

    const errorCallback = () => {
      setIsDeleting(false);
    };

    onDeleteComment(commentId, successCallback, errorCallback);
  };

  if (!comments || comments.length === 0) {
    return null;
  }

  const rootComments = comments.filter((x) => x.replyToCommentId === null);

  return (
    <>
      <div className={containerClassNames}>
        <h2 className="hidden">
          {t("Pages.CollaborativeDocument.Common.Comments")}
        </h2>
        {rootComments.map((rootComment, ix) => {
          const replyComments = comments.filter(
            (x) =>
              x.replyToCommentId !== null &&
              x.replyToCommentId === rootComment.id
          );
          const rootCommentAuthor = participants.find(
            (x) => x.userId === rootComment.authorId
          );

          return (
            <IndividualComment
              key={`${ix}_${rootComment.id}`}
              author={rootCommentAuthor}
              commentId={rootComment.id}
              comment={rootComment.comment}
              commentTimestamp={rootComment.timestamp}
              allowReply={replyComments.length === 0 && !isReadOnly}
              allowDelete={
                !isReadOnly && rootCommentAuthor?.userId === userContext.user.id
              }
              disableDeleteButton={isDeleting}
              onDeleteClick={() => handleDelete(rootComment.id)}
              onTargetCommentForReply={onTargetCommentForReply}
            >
              {replyComments.length > 0 && (
                <div>
                  {replyComments.map((replyComment, replyIx) => {
                    const replyCommentAuthor = participants.find(
                      (x) => x.userId === replyComment.authorId
                    );
                    return (
                      <div
                        className="mt-2"
                        key={`${replyIx}_${replyComment.id}`}
                      >
                        <IndividualComment
                          author={replyCommentAuthor}
                          commentId={replyComment.id}
                          comment={replyComment.comment}
                          commentTimestamp={replyComment.timestamp}
                          allowReply={
                            !isReadOnly && replyIx === replyComments.length - 1
                          }
                          allowDelete={
                            !isReadOnly &&
                            replyCommentAuthor?.userId === userContext.user.id
                          }
                          disableDeleteButton={isDeleting}
                          onDeleteClick={() => handleDelete(replyComment.id)}
                          onTargetCommentForReply={onTargetCommentForReply}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </IndividualComment>
          );
        })}
      </div>
    </>
  );
}

export default CommentsList;
